import type { TopbarButtonBaseProps } from './base/TopbarButton';

import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { ClockIcon, DownloadCircleIcon } from '../../../images/shapes';

import Loader from '../../Loader/Loader';
import TopbarActionButton from './base/TopbarActionButton';
import TopbarButton from './base/TopbarButton';

interface TopbarPublishButtonProps extends TopbarButtonBaseProps {
  /**
   * On publish callback
   */
  onPublish?: () => Promise<void> | void;
  /**
   * On schedule callback
   */
  onSchedule?: () => Promise<void> | void;
  /**
   * If transiting to new status
   */
  isLoading?: boolean;
  /**
   * If button is disabled
   */
  disabled?: boolean;
  /**
   * If button is action button
   */
  actionButton?: boolean;
  /**
   * Tooltip text
   */
  tooltip?: string;
}

const TopbarPublishButton = ({
  onPublish,
  onSchedule,
  isLoading = false,
  disabled = false,
  actionButton = false,
  tooltip,
  ...props
}: TopbarPublishButtonProps) => {
  const { t } = useTranslation();

  if (actionButton) {
    return (
      <>
        <TopbarActionButton
          color="green"
          label={t('ObjectStatus.Transition.publish')}
          disabled={disabled}
          iconImage={<DownloadCircleIcon className="w-3 min-w-3 rotate-180" />}
          onClick={onPublish}
          tooltip={tooltip}
          additionalIconClasses="ml-0.5 mr-3"
          additionalClasses="flex md:hidden"
          testIdSuffix="publish"
        />
        {onSchedule && (
          <TopbarActionButton
            color="purple"
            label={t('ObjectStatus.Transition.schedule')}
            disabled={disabled}
            iconImage={<ClockIcon className="w-3 min-w-3 rotate-180" />}
            onClick={onSchedule}
            tooltip={tooltip}
            additionalIconClasses="ml-0.5 mr-3"
            additionalClasses="flex md:hidden"
            testIdSuffix="set-schedule"
          />
        )}
      </>
    );
  }

  return (
    <div className="flex">
      <TopbarButton
        buttonColor={'green'}
        label={t(`ObjectStatus.Transition.publish`)}
        onClick={onPublish}
        additionalClasses={twMerge(
          'hidden md:flex',
          !!onSchedule && 'rounded-r-none pr-1',
        )}
        disabled={disabled}
        testIdSuffix="publish"
        tooltip={tooltip}
        {...(!onSchedule
          ? { iconImage: <ClockIcon className="w-3 min-w-3 rotate-180" /> }
          : {})}
        {...props}
      />
      {onSchedule && (
        <TopbarButton
          buttonColor={'green'}
          iconImage={
            isLoading ? (
              <Loader size="small" type="spinner-grid" />
            ) : (
              <ClockIcon className="w-3 min-w-3 rotate-180" />
            )
          }
          onClick={onSchedule}
          additionalClasses="hidden md:flex rounded-l-none px-1"
          additionalIconClasses="mr-0"
          disabled={disabled}
          tooltip={tooltip || t('PublicationSchedule.SetSchedule')}
          testIdSuffix="set-schedule"
          {...props}
        />
      )}
    </div>
  );
};

export default TopbarPublishButton;
